import React, { useState, useCallback, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, ChevronLeft, RotateCcw, Shuffle } from "lucide-react";
import useSRS from "../../hooks/useSRS";
import Flashcard from "./Flashcard";
import QuizCard from "./QuizCard";
import VocabLayout from "./VocabLayout";

// ── Mode definitions ────────────────────────────────────────────────
const MODES = [
  { id: "flashcard",    label: "Flashcard",  emoji: "🃏", desc: "Lật thẻ xem nghĩa" },
  { id: "type-meaning", label: "Nhập Nghĩa", emoji: "✍️", desc: "Nhìn từ, gõ nghĩa tiếng Việt" },
  { id: "reverse",      label: "Nhập Từ",    emoji: "🔄", desc: "Nhìn nghĩa, gõ từ tiếng Trung" },
];

// ── SRS rating buttons ──────────────────────────────────────────────
const RATING_BUTTONS = [
  {
    action: "again", label: "Lại",  shortcut: "1", desc: "Chưa nhớ",
    color: "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200",
    feedbackClass: "bg-red-50 text-red-600 border-red-200",
    message: "Cố lên! Sẽ nhớ được thôi 💪",
  },
  {
    action: "good",  label: "Tốt",  shortcut: "2", desc: "+1 cấp",
    color: "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200",
    feedbackClass: "bg-blue-50 text-blue-600 border-blue-200",
    message: "Tốt lắm! Tiếp tục nhé 👍",
  },
  {
    action: "easy",  label: "Dễ",   shortcut: "3", desc: "+2 cấp",
    color: "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200",
    feedbackClass: "bg-green-50 text-green-600 border-green-200",
    message: "Xuất sắc! Bạn học rất nhanh ⚡",
  },
];

export default function FlashcardPage() {
  const [searchParams] = useSearchParams();
  const scope = searchParams.get("scope") || "all";
  const scopeId = searchParams.get("scopeId") || "";

  const {
    currentCard,
    currentIndex,
    totalCards,
    isFlipped,
    loading,
    sessionDone,
    reviewed,
    flipCard,
    handleAction,
    advanceCard,
    restart,
    shuffleCards,
    fetchAll,
    refetchSRS,
  } = useSRS(scope, scopeId);

  // ── Local state ─────────────────────────────────────────────────────
  const [mode,          setMode]          = useState("flashcard");
  const [shakeActive,   setShakeActive]   = useState(false);
  const [microFeedback, setMicroFeedback] = useState(null); // { message, feedbackClass }
  const [quizStats,     setQuizStats]     = useState({ correct: 0, partial: 0, wrong: 0 });

  // ── Mode switching ──────────────────────────────────────────────────
  // Flashcard mode uses SRS-due cards; quiz modes load ALL vocab shuffled
  const handleModeChange = useCallback((newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setQuizStats({ correct: 0, partial: 0, wrong: 0 });
    setMicroFeedback(null);
    if (newMode === "flashcard") {
      refetchSRS();
    } else {
      fetchAll().then(() => shuffleCards());
    }
  }, [mode, refetchSRS, fetchAll, shuffleCards]);

  // ── Flashcard rating with micro-feedback ────────────────────────────
  const handleRating = useCallback(async (action) => {
    const btn = RATING_BUTTONS.find((b) => b.action === action);
    if (btn) {
      setMicroFeedback({ message: btn.message, feedbackClass: btn.feedbackClass });
      setTimeout(() => setMicroFeedback(null), 1600);
    }
    if (action === "again") {
      setShakeActive(true);
      setTimeout(() => setShakeActive(false), 430);
    }
    await handleAction(action);
  }, [handleAction]);

  // ── Quiz handlers ───────────────────────────────────────────────────
  const handleQuizResult = useCallback((result) => {
    setQuizStats((s) => ({ ...s, [result]: s[result] + 1 }));
  }, []);

  const handleQuizNext = useCallback(() => advanceCard(), [advanceCard]);

  // ── Keyboard shortcuts (flashcard mode only) ────────────────────────
  useEffect(() => {
    if (mode !== "flashcard" || loading || sessionDone || totalCards === 0) return;
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        flipCard();
      }
      if (isFlipped) {
        if (e.key === "1") handleRating("again");
        if (e.key === "2") handleRating("good");
        if (e.key === "3") handleRating("easy");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mode, loading, sessionDone, totalCards, isFlipped, flipCard, handleRating]);

  // ── Derived values ──────────────────────────────────────────────────
  const progress = totalCards > 0 ? Math.round((currentIndex / totalCards) * 100) : 0;
  const quizAccuracy = reviewed > 0
    ? Math.round(((quizStats.correct + quizStats.partial * 0.5) / reviewed) * 100)
    : 0;
  const progressBarColor =
    mode === "flashcard"     ? "from-red-400 to-red-600"
    : mode === "type-meaning" ? "from-blue-400 to-blue-600"
    : "from-purple-400 to-purple-600";

  // ── Header ──────────────────────────────────────────────────────────
  const pageHeader = (
    <>
      <Link
        to="/vocabulary"
        className="flex items-center gap-1 text-gray-500 hover:text-gray-800 transition-colors text-sm"
      >
        <ChevronLeft size={18} />
        Danh sách từ
      </Link>
      <div className="flex items-center gap-2 ml-auto">
        <BookOpen size={18} className="text-red-600" />
        <span className="font-semibold text-gray-900 text-sm">Ôn tập từ vựng</span>
      </div>
    </>
  );

  return (
    <VocabLayout header={pageHeader} mainClassName="flex-1 flex flex-col items-center p-6 gap-5">

      {/* ── Mode tabs ─────────────────────────────────────────────────── */}
      {!loading && (
        <div className="w-full max-w-2xl">
          <div className="flex gap-1.5 p-1 bg-gray-100 rounded-2xl">
            {MODES.map(({ id, label, emoji, desc }) => (
              <button
                key={id}
                onClick={() => handleModeChange(id)}
                title={desc}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                  mode === id
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
                }`}
              >
                <span className="text-base leading-none">{emoji}</span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      {loading ? (
        /* Loading skeleton */
        <div className="w-full max-w-2xl flex flex-col items-center gap-5">
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-gray-200 rounded-full animate-pulse" />
          </div>
          <div className="w-full h-[320px] rounded-3xl bg-gray-100 animate-pulse" />
          <p className="text-gray-400 text-sm">Đang tải thẻ ôn tập...</p>
        </div>

      ) : sessionDone || totalCards === 0 ? (
        /* ── Session complete / no cards ────────────────────────────────── */
        <div className="w-full max-w-md flex flex-col items-center gap-6 text-center animate-pop-in mt-6">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center text-5xl shadow-lg ${
            totalCards === 0 ? "bg-blue-50" : "bg-green-100"
          }`}>
            {totalCards === 0 ? "🚀" : "🎉"}
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {totalCards === 0 ? "Không có thẻ nào!" : "Hoàn thành phiên học!"}
            </h2>
            <p className="text-gray-500 leading-relaxed">
              {totalCards === 0
                ? "Tất cả từ đã ôn đúng lịch SRS. Nghỉ ngơi chút nhé! ☕"
                : mode === "flashcard"
                  ? `Bạn đã ôn ${reviewed} thẻ. Xuất sắc! Hãy duy trì mỗi ngày.`
                  : `${reviewed} từ đã trả lời · độ chính xác ${quizAccuracy}%.`}
            </p>
          </div>

          {/* Flashcard stats */}
          {sessionDone && mode === "flashcard" && (
            <div className="flex gap-3 w-full justify-center">
              <div className="flex-1 max-w-[110px] py-3 bg-gray-50 rounded-2xl border border-gray-100 text-center">
                <div className="text-2xl font-bold text-gray-900">{reviewed}</div>
                <div className="text-xs text-gray-500 mt-0.5">Thẻ ôn</div>
              </div>
              <div className="flex-1 max-w-[110px] py-3 bg-green-50 rounded-2xl border border-green-100 text-center">
                <div className="text-2xl font-bold text-green-700">100%</div>
                <div className="text-xs text-gray-500 mt-0.5">Hoàn thành</div>
              </div>
            </div>
          )}

          {/* Quiz stats */}
          {sessionDone && mode !== "flashcard" && (
            <div className="flex gap-2 w-full">
              <div className="flex-1 py-3 bg-green-50 rounded-2xl border border-green-100 text-center">
                <div className="text-xl font-bold text-green-700">{quizStats.correct}</div>
                <div className="text-xs text-gray-500 mt-0.5">Đúng</div>
              </div>
              <div className="flex-1 py-3 bg-yellow-50 rounded-2xl border border-yellow-100 text-center">
                <div className="text-xl font-bold text-yellow-600">{quizStats.partial}</div>
                <div className="text-xs text-gray-500 mt-0.5">Gần đúng</div>
              </div>
              <div className="flex-1 py-3 bg-red-50 rounded-2xl border border-red-100 text-center">
                <div className="text-xl font-bold text-red-600">{quizStats.wrong}</div>
                <div className="text-xs text-gray-500 mt-0.5">Sai</div>
              </div>
              <div className="flex-1 py-3 bg-blue-50 rounded-2xl border border-blue-100 text-center">
                <div className="text-xl font-bold text-blue-700">{quizAccuracy}%</div>
                <div className="text-xs text-gray-500 mt-0.5">Chính xác</div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/vocabulary"
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Về danh sách
            </Link>
            {sessionDone && (
              <button
                onClick={() => {
                  setQuizStats({ correct: 0, partial: 0, wrong: 0 });
                  if (mode === "flashcard") restart();
                  else shuffleCards();
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 active:scale-95 transition-all"
              >
                <RotateCcw size={15} />
                Làm lại
              </button>
            )}
            <button
              onClick={() => {
                setQuizStats({ correct: 0, partial: 0, wrong: 0 });
                fetchAll().then(() => { if (mode !== "flashcard") shuffleCards(); });
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 active:scale-95 transition-all"
            >
              <BookOpen size={15} />
              Ôn tất cả
            </button>
          </div>
        </div>

      ) : (
        /* ── Active session ─────────────────────────────────────────────── */
        <div className="w-full max-w-2xl flex flex-col items-center gap-5">

          {/* Progress */}
          <div className="w-full">
            <div className="flex justify-between items-center text-xs mb-1.5">
              <span className="font-semibold text-gray-700 text-sm">
                {currentIndex + 1} / {totalCards}
              </span>
              <div className="flex items-center gap-3 text-gray-400">
                {mode !== "flashcard" && (
                  <button
                    onClick={() => { shuffleCards(); setQuizStats({ correct: 0, partial: 0, wrong: 0 }); }}
                    className="flex items-center gap-1 hover:text-gray-700 transition-colors"
                    title="Xáo trộn lại"
                  >
                    <Shuffle size={12} />
                    <span className="text-xs">Xáo trộn</span>
                  </button>
                )}
                <span className="font-medium">{progress}%</span>
              </div>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 bg-gradient-to-r ${progressBarColor}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Card — key change triggers remount + slide-in animation */}
          <div
            key={`${currentIndex}-${mode}`}
            className={`w-full ${shakeActive ? "animate-shake" : "animate-slide-in-right"}`}
          >
            {mode === "flashcard" ? (
              <Flashcard card={currentCard} isFlipped={isFlipped} onClick={flipCard} />
            ) : (
              <QuizCard
                card={currentCard}
                mode={mode}
                onResult={handleQuizResult}
                onNext={handleQuizNext}
              />
            )}
          </div>

          {/* Flashcard: micro-feedback + rating buttons */}
          {mode === "flashcard" && (
            <>
              {microFeedback ? (
                <div className={`animate-fade-up text-sm font-medium px-5 py-1.5 rounded-full border ${microFeedback.feedbackClass}`}>
                  {microFeedback.message}
                </div>
              ) : !isFlipped ? (
                <p className="text-xs text-gray-400">
                  Nhấn thẻ để lật ·{" "}
                  <kbd className="bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded text-gray-500">Space</kbd>
                </p>
              ) : null}

              {isFlipped && !microFeedback && (
                <div className="w-full flex flex-col gap-2">
                  <p className="text-xs text-gray-400 text-center">
                    Bạn nhớ đến mức nào? ·{" "}
                    {["1","2","3"].map((k) => (
                      <kbd key={k} className="bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded text-gray-500 mx-0.5">{k}</kbd>
                    ))}
                  </p>
                  <div className="w-full grid grid-cols-3 gap-3">
                    {RATING_BUTTONS.map(({ action, label, shortcut, desc, color }) => (
                      <button
                        key={action}
                        onClick={() => handleRating(action)}
                        className={`flex flex-col items-center py-3.5 px-2 rounded-2xl text-sm font-semibold transition-all active:scale-95 ${color}`}
                      >
                        <span className="text-[10px] text-gray-400 font-normal mb-0.5">{shortcut}</span>
                        <span>{label}</span>
                        <span className="text-xs font-normal opacity-60 mt-0.5">{desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Quiz: live score ticker */}
          {mode !== "flashcard" && currentIndex > 0 && (
            <div className="flex items-center gap-4 text-xs font-semibold">
              <span className="text-green-600">✓ {quizStats.correct}</span>
              {quizStats.partial > 0 && <span className="text-yellow-500">≈ {quizStats.partial}</span>}
              <span className="text-red-500">✗ {quizStats.wrong}</span>
            </div>
          )}
        </div>
      )}
    </VocabLayout>
  );
}
